import { useEffect, useState } from "react";
import { LoaderCircle, Search, X } from "lucide-react";
import clsx from "clsx";
import Input from "./InputField";

export interface InputSearchProps {
  loading: boolean;
  inputClassName?: string;
  loaderClassName?: string;
  clearClassName?: string;
  onSearch: (value: string) => void;
}

export const InputSearch = ({
  loading,
  inputClassName,
  loaderClassName,
  clearClassName,
  onSearch,
}: InputSearchProps) => {
  const [value, setValue] = useState<string>("");
  const [internalLoading, setInternalLoading] = useState(false);

  const handleOnChange = (value: string) => {
    setValue(value);
    onSearch(value);
    setInternalLoading(true);
  };

  useEffect(() => {
    if (!loading) {
      setInternalLoading(false);
    }
  }, [loading]);

  const handleClear = () => {
    setValue("");
    onSearch("");
  };

  const isSearching = internalLoading && value.length > 0;

  const hasValue = value.length > 0;

  return (
    <div className="relative flex justify-center">
      <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
        <Search size={16} className="text-gray-400" />
      </div>

      <Input
        value={value}
        placeholder="Search"
        sizeType="sm"
        className={clsx(
          "pl-10 text-gray-400 w-full focus:w-80 transition-[width] duration-200 ease-in-out",
          hasValue ? (isSearching ? "pr-10" : "pr-5") : "",
          inputClassName,
        )}
        onChange={(e) => handleOnChange(e.target.value)}
      />

      <div
        className={clsx(
          "absolute inset-y-0 right-6 flex items-center pointer-events-none animate-spin opacity-0",
          { "opacity-100": isSearching },
          loaderClassName,
        )}
      >
        <LoaderCircle size={14} />
      </div>

      <button
        className={clsx(
          "absolute inset-y-0 right-1 flex items-center cursor-pointer opacity-0",
          { "opacity-100": hasValue },
          clearClassName,
        )}
        onClick={handleClear}
      >
        <X size={14} />
      </button>
    </div>
  );
};
